import { getApiBaseUrlWithoutPath } from '../../config/environment';
import environment from '../../config/environment';
import { getAdminToken, getAuthHeaders } from './authUtils';

async function parseUploadJson(response) {
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const msg =
      result.message ||
      result.error ||
      (typeof result.errors === 'string' ? result.errors : null) ||
      `Upload failed (${response.status})`;
    const err = new Error(msg);
    err.status = response.status;
    err.body = result;
    throw err;
  }

  const uploads = result.data?.uploads ?? result.uploads ?? result.data?.files;
  const first = Array.isArray(uploads) ? uploads[0] : null;
  const url =
    first?.url ||
    first?.secure_url ||
    result.data?.url ||
    result.data?.creatorBannerUrl ||
    result.url;

  if (!url) {
    throw new Error(result.message || 'No image URL returned');
  }

  return { url, raw: first || result.data || result };
}

async function postMultipartUpload(endpoint, file, extraFields = {}) {
  const token = getAdminToken();
  if (!token) {
    const err = new Error('Upload requires an admin session.');
    err.status = 401;
    throw err;
  }

  const apiUrl = getApiBaseUrlWithoutPath();
  const formData = new FormData();
  formData.append('images', file);
  formData.append('image', file);
  Object.entries(extraFields).forEach(([key, value]) => {
    if (value != null && value !== '') formData.append(key, value);
  });

  const response = await fetch(`${apiUrl}${endpoint}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  return parseUploadJson(response);
}

async function uploadViaCloudinaryUnsigned(file) {
  const cloudName = (environment.CLOUDINARY_CLOUD_NAME || '').trim();
  const preset = (environment.CLOUDINARY_UPLOAD_PRESET || '').trim();
  if (!cloudName || !preset) {
    return null;
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', preset);
  formData.append('folder', 'sheen-wear/creator-banners');

  const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
    method: 'POST',
    body: formData,
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.secure_url) {
    const err = new Error(result.error?.message || 'Cloudinary upload failed');
    err.status = response.status;
    throw err;
  }

  return { url: result.secure_url, raw: result };
}

/**
 * Creator shop header — tries team-safe routes first, then generic admin upload, then Cloudinary preset.
 */
export async function uploadCreatorBannerImage(file) {
  const attempts = [
    () => postMultipartUpload('/api/admin/profile/creator-banner', file),
    () => postMultipartUpload('/api/admin/profile/upload-banner', file),
    () => postMultipartUpload('/api/admin/upload', file, { purpose: 'creatorBanner' }),
    () => uploadViaCloudinaryUnsigned(file),
  ];

  let lastError = null;

  for (const run of attempts) {
    try {
      const result = await run();
      if (result?.url) return result;
    } catch (err) {
      lastError = err;
      if (err.status === 401) throw err;
    }
  }

  if (lastError?.status === 403) {
    throw new Error(
      'Upload blocked for team accounts (403). Backend must allow POST /api/admin/profile/creator-banner for role team — see docs/BACKEND_PROMPT_CREATOR_STOREFRONT.md.'
    );
  }

  throw lastError || new Error('Banner upload failed');
}

/** Generic admin image upload (store logo, categories, etc.). */
export async function uploadAdminImage(file, extraFields = {}) {
  const headers = getAuthHeaders();
  if (!headers.Authorization) {
    throw new Error('Upload requires an admin session.');
  }
  return postMultipartUpload('/api/admin/upload', file, extraFields);
}

async function uploadViaCloudinaryUnsignedPromo(file) {
  const cloudName = (environment.CLOUDINARY_CLOUD_NAME || '').trim();
  const preset = (environment.CLOUDINARY_UPLOAD_PRESET || '').trim();
  if (!cloudName || !preset) {
    return null;
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', preset);
  formData.append('folder', 'sheen-wear/shop-promos');

  const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
    method: 'POST',
    body: formData,
  });

  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.secure_url) {
    const err = new Error(result.error?.message || 'Cloudinary upload failed');
    err.status = response.status;
    throw err;
  }

  return { url: result.secure_url, raw: result };
}

/**
 * Team shop promotional popup — team-safe routes first (403 on generic /admin/upload is common).
 */
export async function uploadShopPromoPopupImage(file) {
  const attempts = [
    () => postMultipartUpload('/api/admin/profile/promo-popup-image', file),
    () => postMultipartUpload('/api/admin/profile/promo-popup', file),
    () => postMultipartUpload('/api/admin/upload', file, { purpose: 'shopPromoPopup' }),
    () => uploadViaCloudinaryUnsignedPromo(file),
  ];

  let lastError = null;

  for (const run of attempts) {
    try {
      const result = await run();
      if (result?.url) return result;
    } catch (err) {
      lastError = err;
      if (err.status === 401) throw err;
    }
  }

  if (lastError?.status === 403) {
    throw new Error(
      'Upload blocked for team accounts (403). Backend must allow POST /api/admin/profile/promo-popup-image for role team — see docs/BACKEND_PROMPT_SHOPKEEPER_PROMO_UPLOAD.md.'
    );
  }

  throw lastError || new Error('Promo image upload failed');
}
